import type { LDClient } from 'launchdarkly-react-client-sdk';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { hasTutor, subscriptionsHaveTutoringHours as hasTutoringHours } from '../lib/user/user';

import { useUserContext } from '@nrdy-marketing-engine/server/src/user/context';
import useUserSubscriptionData from '@nrdy-marketing-engine/server/src/user/hooks/use-user-subscription-data';
import trackCartInteraction from '../api/track/track-cart-interaction';
import useInsensitiveSearchParams from './use-insensitive-search-params';

const crpFlow = '/my-learning/tutors/new';
const myLearningFlow = '/my-learning/profile/select';
const onboardingFlow = '/my-learning/welcome';

const useCheckoutCompletionRouting = ({ ldClient }: { ldClient?: LDClient } = {}) => {
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const params = useSearchParams();
  const { user, loading: userLoading, students, studentsError } = useUserContext();

  const userIdForStudent = user?.students?.[0]?.user_id;
  const onboardingFlagName = 'ACT-417-onboarding-wizard';
  const onboardingFlag = ldClient?.variation(
    onboardingFlagName,
    '' // empty string is the OFF value for the flag
  );
  const hasOnboarding =
    typeof onboardingFlag === 'string' &&
    onboardingFlag.includes('variant') &&
    Boolean(params.get('q'));

  const {
    userSubscriptions,
    refetch: fetchSubscriptionData,
    fetchError: userSubscriptionsFetchError,
    loading: userSubscriptionsLoading,
  } = useUserSubscriptionData(userIdForStudent);

  const subscriptionsHaveTutoringHours = hasTutoringHours(userSubscriptions);

  const fetchingIsComplete = userSubscriptions && !userLoading;
  const fetchesLoading = userSubscriptionsLoading || userLoading;
  const hasSubscriptionsFetchError = !!userSubscriptionsFetchError;
  const errorsEncounteredDuringFetching = hasSubscriptionsFetchError || studentsError;
  const router = useRouter();
  const enrollmentItemId = useInsensitiveSearchParams().get('enrollmentItemId');

  const _track = useCallback(
    (category: string, reason: string, metadata: Record<string, any> = undefined) =>
      trackCartInteraction(category, reason, 'checkout-completion-routing', {
        client_uuid: user?.entity.client_uuid,
        metadata,
      }),
    [user]
  );

  const redirect = useCallback(
    (url: string): void => {
      router.replace(url);
    },
    [router]
  );

  useEffect(
    function _trackStudentData() {
      async function _trackStudentInfo() {
        try {
          if (studentsError) throw studentsError;
          await _track('checkout_routing_student_data_fetch_success', 'success');
        } catch (error) {
          await _track('checkout_routing_student_data_fetch_failure', 'error', { error });
        }
      }

      if (!students?.length && fetchTrigger && !hasSubscriptionsFetchError) _trackStudentInfo();
    },
    [_track, students, studentsError, fetchTrigger, hasSubscriptionsFetchError, user]
  );

  useEffect(
    function _fetchUserSubscriptions() {
      async function _fetchUserSubs() {
        try {
          await fetchSubscriptionData();
          await _track('checkout_routing_user_subscriptions_fetch_success', 'success');
        } catch (error) {
          await _track('checkout_routing_user_subscriptions_fetch_failure', 'error', { error });
        }
      }

      if (!userSubscriptions && fetchTrigger && userIdForStudent && !userSubscriptionsFetchError)
        _fetchUserSubs();
    },
    [
      _track,
      userSubscriptions,
      fetchTrigger,
      userIdForStudent,
      userSubscriptionsFetchError,
      fetchSubscriptionData,
      user,
    ]
  );

  useEffect(
    function _triggerToggleChecker() {
      if (fetchingIsComplete || errorsEncounteredDuringFetching) {
        setFetchTrigger(false);
      }
    },
    [errorsEncounteredDuringFetching, fetchingIsComplete]
  );

  useEffect(
    function _routeUserIfNecessary() {
      const _trackFailureAndRedirect = (reason: string, route: string, errors: any[]) => {
        _track('checkout_routing_error', reason, { errors, route, userIdForStudent });
        redirect(route);
      };

      const _trackSuccessAndRedirect = (reason: string, route: string) => {
        const studentHasTutor = hasTutor(students);
        _track('checkout_routing_success', reason, {
          route,
          studentHasTutor,
          subscriptionsHaveTutoringHours,
          userIdForStudent,
        });
        redirect(route);
      };

      if (!fetchingIsComplete || fetchesLoading || !fetchTrigger || !userIdForStudent) return;

      if (errorsEncounteredDuringFetching) {
        return _trackFailureAndRedirect('error during fetch', crpFlow, [
          userSubscriptionsFetchError,
        ]);
      }

      if (enrollmentItemId) {
        return _trackSuccessAndRedirect(
          'user is enrolling in a course',
          `/membership/enroll/${enrollmentItemId}?thankyou=1`
        );
      }

      if (hasOnboarding) {
        return _trackSuccessAndRedirect(
          'student has a quote and onboarding is enabled',
          onboardingFlow
        );
      }

      if (!subscriptionsHaveTutoringHours || hasTutor(students)) {
        return _trackSuccessAndRedirect(
          'subscription does not have tutoring hours or student has a tutor',
          myLearningFlow
        );
      }

      return _trackSuccessAndRedirect(
        'subscription has tutoring hours and student has no tutor',
        crpFlow
      );
    },
    [
      _track,
      enrollmentItemId,
      errorsEncounteredDuringFetching,
      fetchTrigger,
      fetchesLoading,
      fetchingIsComplete,
      hasOnboarding,
      redirect,
      subscriptionsHaveTutoringHours,
      user,
      userIdForStudent,
      students,
      userSubscriptionsFetchError,
    ]
  );

  useEffect(
    function _userWithNoStudents() {
      const url = hasOnboarding ? onboardingFlow : myLearningFlow;

      const _trackCatchAllRouting = () =>
        _track(
          'checkout_routing_catch_all',
          hasOnboarding
            ? `userIdForStudent is undefined and onboarding is enabled routing to ${url}`
            : `userIdForStudent is undefined and fetches are done loading routing to ${url}`,
          {
            route: myLearningFlow,
            userIdForStudent,
          }
        );

      const fetchesLoading = userSubscriptionsLoading || userLoading;

      // catch all for possible a user with no students
      if (fetchTrigger && user && !userLoading && !userIdForStudent && !fetchesLoading) {
        _trackCatchAllRouting();
        return redirect(url);
      }
    },
    [
      _track,
      fetchTrigger,
      hasOnboarding,
      redirect,
      user,
      userIdForStudent,
      userLoading,
      userSubscriptionsLoading,
    ]
  );

  const enableRoutingEffects = useCallback(() => setFetchTrigger(true), []);

  return { enableRoutingEffects };
};

export default useCheckoutCompletionRouting;
